<script>
import settings from '@shared/settings'
import store from '@shared/store'

export default {
  data() {
    return {
      reference: '',
      digest: '',
      loading: false,
      signing: false,
      notAvailable: false,
      notAvailableReason: undefined,
      authenticationFailed: false,
      authenticationFailedReason: undefined,
      isDesktop: settings.isDesktopWeb(),
      isComfort: false
    }
  },
  created() {
    this.$root.$on('certificate-exists', this.onCertificateExists)
    this.$root.$on('csr-generated', this.onCsrGenerated)
    this.$root.$on('certificate-stored', this.onCertificateStored)
    this.$root.$on('signed', this.onSigned)
    this.$root.$on('fallback-to-otp', this.onFallbackToOtp)
    this.$root.$on('user-cancelled', this.onUserCancelled)
    this.$root.$on('not-available', this.onNotAvailable)
    this.$root.$on('authentication-failed', this.onAuthenticationFailed)
  },
  destroyed() {
    this.$root.$off('certificate-exists', this.onCertificateExists)
    this.$root.$off('csr-generated', this.onCsrGenerated)
    this.$root.$off('certificate-stored', this.onCertificateStored)
    this.$root.$off('signed', this.onSigned)
    this.$root.$off('fallback-to-otp', this.onFallbackToOtp)
    this.$root.$off('user-cancelled', this.onUserCancelled)
    this.$root.$off('not-available', this.onNotAvailable)
    this.$root.$off('authentication-failed', this.onAuthenticationFailed)
  },
  methods: {
    setReference(reference) {
      this.reference = reference
      return this.prepareUnsignedDocument()
    },
    setComfort() {
      this.isComfort = true
    },
    calculateDraft(reference, loan) {
      const parameters = { amount: loan.amount, periodInMonths: loan.periodInMonths }
      return this.$http.post(`/api/applications/${reference}/draft`, parameters).then((response) => {
        const draft = response.data
        const storedDraft = { loan: draft }
        if (this.$configuration.newCoreEnabled && this.requireMissingConsents) {
          storedDraft.marketingConsent = this.marketingConsent
        }
        if (this.$configuration.newCoreEnabled && this.requirePepCheck) {
          storedDraft.notPep = this.notPep
        }
        store.setDraft(storedDraft)
        this.loading = false
      })
    },
    prepareUnsignedDocument() {
      return this.$http.put(`/api/loans/${this.reference}/unsigned-contract/prepare`)
    },
    initializeSigning() {
      if (this.isDesktop) {
        this.$router.push({ name: 'returning-customer-sign-otp' })
      } else {
        const parameters = JSON.stringify(this.customerParameters(this.biometryLabels('createCertificate')))

        if (settings.isAndroid()) {
          this.signing = true
          // this.notAvailable = false
          // this.notAvailableReason = undefined
          this.authenticationFailed = false
          this.authenticationFailedReason = undefined
          window.publicKeyInfrastructure.initializeSigning(parameters)
        } else if (settings.isIOS()) {
          this.signing = true
          // this.notAvailable = false
          // this.notAvailableReason = undefined
          this.authenticationFailed = false
          this.authenticationFailedReason = undefined
          window.webkit.messageHandlers.pkiInitializeSigning.postMessage(parameters)
        }
      }
    },
    onCertificateExists() {
      this.signContract()
    },
    signContract() {
      const customerData = store.state.currentUser
      this.dataToSign(customerData, this.reference).then((digest) => {
        this.digest = digest
        const parameters = JSON.stringify({
          ...this.customerParameters(this.biometryLabels('signLoanContract')),
          dataToSign: this.digest
        })

        if (settings.isAndroid()) {
          window.publicKeyInfrastructure.signPDF(parameters)
        } else if (settings.isIOS()) {
          window.webkit.messageHandlers.pkiSignPDF.postMessage(parameters)
        }
      })
    },
    dataToSign(customer, reference) {
      return this.$http.get(`/api/pki/${customer.pin}/contracts/${reference}/data-to-sign`).then(response => {
        return response.data.digest
      })
    },
    customerParameters(labels = {}) {
      const customerData = store.state.currentUser
      return {
        firstName: customerData.firstName,
        lastName: customerData.lastName,
        pin: customerData.pin,
        birthDate: customerData.birthDate,
        labels: labels
      }
    },
    biometryLabels(prefix) {
      return {
        title: this.$t(`biometry.${prefix}.title`),
        subtitle: this.$t(`biometry.${prefix}.subtitle`),
        description: this.$t(`biometry.${prefix}.description`),
        cancel: this.$t(`biometry.${prefix}.cancel`)
      }
    },
    onCsrGenerated(csr) {
      const customerData = store.state.currentUser
      this.$http.post(`/api/pki/${customerData.pin}/issue-certificate`, csr).then((response) => {
        const certificate = response.data.certificate
        const parameters = JSON.stringify({ ...this.customerParameters(), certificate })

        if (settings.isAndroid()) {
          window.publicKeyInfrastructure.storeIssuedCertificate(parameters)
        } else if (settings.isIOS()) {
          window.webkit.messageHandlers.pkiStoreIssuedCertificate.postMessage(parameters)
        }
      })
    },
    onCertificateStored() {
      this.signContract()
    },
    onSigned(payload) {
      const customerData = store.state.currentUser
      const reference = this.reference
      this.$http.post(`/api/pki/${customerData.pin}/contracts/${reference}/add-signature`, {
        reference: reference,
        digest: this.digest,
        signature: payload.signature // signature is a Base64 string already
      }).then(() => {
        if (this.isComfort) {
          this.handleComfortLoanApplication()
        } else {
          this.handleRegularLoanApplication()
        }
      })
    },
    redirectToNextPage: function () {
      this.$root.$emit('enable-biometric-login')
      const nextPage = settings.featureFlags.isSurveyEnabled() ? 'signing-survey' : 'customer-overview'
      this.$router.replace({ name: 'no-back', params: { page: nextPage } })
    },
    handleRegularLoanApplication() {
      return this.confirmSignedRegularLoanApplication().catch(() => {
        this.$errorToast(this.$t('errors.signing.title'), this.$t('errors.signing.content'))
      }).finally(() => {
        this.redirectToNextPage()
      })
    },
    handleComfortLoanApplication() {
      return this.confirmSignedComfortLoanApplication().catch(() => {
        this.$errorToast(this.$t('errors.application.title'), this.$t('errors.application.content'))
      }).finally(() => {
        this.redirectToNextPage()
      })
    },
    confirmSignedComfortLoanApplication() {
      const customerData = store.state.currentUser
      const draft = store.getDraft()
      const parameters = {
        customerPin: customerData.pin,
        reference: this.reference,
        providedHash: draft.loan.hash,
        loan: {
          amount: draft.loan.amount,
          periodInMonths: draft.loan.periodInMonths,
          productId: this.reference
        }
      }
      return this.$http.post(`/api/applications/${(this.reference)}/signed/comfort`, parameters)
          .then(this.clearApplication)
    },
    confirmSignedRegularLoanApplication() {
      const customerData = store.state.currentUser
      const draft = store.getDraft()
      const parameters = {
        customerPin: customerData.pin,
        reference: draft.loan.reference,
        providedHash: draft.loan.hash,
        loan: {
          amount: draft.loan.amount,
          periodInMonths: draft.loan.periodInMonths,
          productId: draft.loan.reference
        }
      }
      return this.$http.post(`/api/applications/${(draft.loan.reference)}/signed`, parameters)
    },
    onFallbackToOtp() {
      this.$router.push({ name: 'returning-customer-sign-otp' })
    },
    onUserCancelled() {
      this.signing = false
    },
    onNotAvailable(payload) {
      this.signing = false
      this.notAvailable = true
      this.notAvailableReason = payload.reason

      this.$router.push({ name: 'returning-customer-sign-otp' })
    },
    onAuthenticationFailed(payload) {
      this.signing = false
      this.authenticationFailed = true
      this.authenticationFailedReason = payload.reason
    }
  },
  computed: {
    notAvailableError() {
      if (settings.isAndroid()) {
        return this.$t('SignContractPage.notAvailableError.android')
      } else if (settings.isIOS()) {
        return this.$t('SignContractPage.notAvailableError.ios')
      } else {
        return this.$t('SignContractPage.notAvailableError.default')
      }
    },
    addNotAvailableReason() {
      if (!this.notAvailableReason) return ''
      return ` (${this.notAvailableReason})`
    },
    addAuthenticationFailedReason() {
      if (!this.authenticationFailedReason) return ''
      return ` (${this.authenticationFailedReason})`
    }
  }
}
</script>
